import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Loader2,
  MoreHorizontal,
  Plus,
  KeyRound,
  Power,
  Trash2,
  Save,
  Crown,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

const EMPTY_FORM = {
  email: "",
  name: "",
  contact_name: "",
  password: "",
  status: "active",
  assigned_customer_ids: [],
};

export default function PortalUsersPage() {
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [subs, setSubs] = useState({}); // portal_user_id → subscription summary
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = creating
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [pendingOverride, setPendingOverride] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, c, s] = await Promise.all([
        api.get("/admin/portal-users"),
        api.get("/customers"),
        api.get("/admin/subscriptions"),
      ]);
      setUsers(u.data.users || []);
      setBusinesses(c.data.customers || []);
      const subMap = {};
      (s.data.subscriptions || []).forEach((row) => {
        subMap[row.portal_user_id] = row;
      });
      setSubs(subMap);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const businessById = useMemo(() => {
    const m = new Map();
    businesses.forEach((b) => m.set(b.id, b));
    return m;
  }, [businesses]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name || "",
      contact_name: u.contact_name || "",
      password: "",
      status: u.status,
      assigned_customer_ids: u.assigned_customer_ids || [],
    });
    setDialogOpen(true);
  };

  const toggleAssigned = (id) => {
    setForm((f) => ({
      ...f,
      assigned_customer_ids: f.assigned_customer_ids.includes(id)
        ? f.assigned_customer_ids.filter((x) => x !== id)
        : [...f.assigned_customer_ids, id],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/portal-users/${editing.id}`, {
          email: form.email,
          name: form.name,
          contact_name: form.contact_name || null,
          status: form.status,
          assigned_customer_ids: form.assigned_customer_ids,
        });
        toast.success("Benutzer aktualisiert.");
      } else {
        await api.post("/admin/portal-users", {
          email: form.email,
          name: form.name,
          contact_name: form.contact_name || null,
          password: form.password,
          status: form.status,
          assigned_customer_ids: form.assigned_customer_ids,
        });
        toast.success("Benutzer angelegt.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/admin/portal-users/${u.id}`, {
        status: u.status === "active" ? "inactive" : "active",
      });
      toast.success(u.status === "active" ? "Deaktiviert." : "Aktiviert.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/portal-users/${u.id}`);
      toast.success("Benutzer gelöscht.");
      setUsers((us) => us.filter((x) => x.id !== u.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/portal-users/${resetTarget.id}/reset-password`, {
        new_password: newPw,
      });
      toast.success("Passwort zurückgesetzt.");
      setResetTarget(null);
      setNewPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const applyOverride = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/subscriptions/${overrideTarget.id}/override`, {
        plan_override: pendingOverride || null,
      });
      toast.success(pendingOverride ? "Override gesetzt." : "Override entfernt.");
      setOverrideTarget(null);
      setPendingOverride("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-users-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Kundenportal</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
            Kunden-Zugänge
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Verwalte die Portal-Logins deiner Kunden und deren zugewiesene Firmen.
          </p>
        </div>
        <Button
          className="rounded-full h-11 px-5 gap-2"
          onClick={openCreate}
          data-testid="portal-users-new-btn"
        >
          <Plus className="w-4 h-4" /> Neuer Zugang
        </Button>
      </header>

      <div className="card-surface">
        {loading ? (
          <div className="py-16 text-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lädt…
          </div>
        ) : users.length ? (
          <ul className="divide-y divide-white/[0.05]">
            {users.map((u) => (
              <li
                key={u.id}
                data-testid={`portal-user-row-${u.id}`}
                className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{u.name}</span>
                    <Badge
                      variant="outline"
                      className={
                        u.status === "active"
                          ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                          : "border-white/10 text-muted-foreground"
                      }
                    >
                      {u.status === "active" ? "Aktiv" : "Inaktiv"}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {u.contact_name ? `${u.contact_name} · ` : ""}
                    {(u.assigned_customer_ids || []).length} Firma(en) ·
                    letzter Login: {fmt(u.last_login_at)}
                  </div>
                  {subs[u.id] ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                      <span className="text-muted-foreground">Tarif:</span>
                      <Badge
                        variant="outline"
                        className={
                          subs[u.id].override_active
                            ? "border-primary/40 text-primary bg-primary/10"
                            : "border-white/10 text-muted-foreground"
                        }
                        data-testid={`sub-plan-badge-${u.id}`}
                      >
                        {subs[u.id].override_active ? (
                          <Crown className="w-3 h-3 mr-1" />
                        ) : null}
                        {subs[u.id].plan?.name || "—"}
                      </Badge>
                      <span className="text-muted-foreground">·</span>
                      <span
                        className="text-muted-foreground"
                        data-testid={`sub-status-${u.id}`}
                      >
                        {subs[u.id].subscription_status}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span
                        className="text-muted-foreground"
                        data-testid={`sub-usage-${u.id}`}
                      >
                        {subs[u.id].usage?.assigned_active}/
                        {subs[u.id].usage?.location_limit} aktiv
                      </span>
                      {subs[u.id].stripe_customer_id ? (
                        <span className="text-muted-foreground font-mono text-[10px] truncate max-w-[160px]">
                          {subs[u.id].stripe_customer_id}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {(u.assigned_customer_ids || []).length ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {u.assigned_customer_ids.map((cid) => (
                        <Badge
                          key={cid}
                          variant="outline"
                          className="border-white/10 text-xs font-normal"
                        >
                          {businessById.get(cid)?.business_name || cid}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 self-end sm:self-center"
                      data-testid={`portal-user-actions-${u.id}`}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-popover">
                    <DropdownMenuItem
                      onClick={() => openEdit(u)}
                      data-testid={`portal-user-edit-${u.id}`}
                    >
                      Bearbeiten
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setResetTarget(u)}
                      data-testid={`portal-user-reset-${u.id}`}
                    >
                      <KeyRound className="w-4 h-4 mr-2" /> Passwort zurücksetzen
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setOverrideTarget(u);
                        setPendingOverride(subs[u.id]?.plan_override || "");
                      }}
                      data-testid={`portal-user-override-${u.id}`}
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" /> Tarif-Override
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => toggleStatus(u)}
                      data-testid={`portal-user-toggle-${u.id}`}
                    >
                      <Power className="w-4 h-4 mr-2" />
                      {u.status === "active" ? "Deaktivieren" : "Aktivieren"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                          onSelect={(e) => e.preventDefault()}
                          className="text-destructive focus:text-destructive"
                          data-testid={`portal-user-delete-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Löschen
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Zugang wirklich löschen?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {u.email} verliert dauerhaft den Zugriff.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => remove(u)}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid={`portal-user-delete-confirm-${u.id}`}
                          >
                            Endgültig löschen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-14 text-center text-sm text-muted-foreground">
            Noch keine Portal-Zugänge angelegt.
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-card border-white/[0.06]">
          <form onSubmit={save} data-testid="portal-user-dialog-form">
            <DialogHeader>
              <DialogTitle>{editing ? "Zugang bearbeiten" : "Neuen Zugang anlegen"}</DialogTitle>
              <DialogDescription>
                Der Kunde meldet sich unter <span className="font-mono">/login</span> an
                und wird automatisch ins Kundenportal geleitet.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <FormField label="Firmenname (Anzeigename)">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="bg-background/50"
                    data-testid="portal-user-input-name"
                  />
                </FormField>
                <FormField label="Ansprechpartner">
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    className="bg-background/50"
                    data-testid="portal-user-input-contact"
                  />
                </FormField>
              </div>
              <FormField label="E-Mail">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="bg-background/50"
                  data-testid="portal-user-input-email"
                />
              </FormField>
              {!editing && (
                <FormField label="Passwort" hint="Mindestens 8 Zeichen.">
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    minLength={8}
                    required
                    className="bg-background/50"
                    data-testid="portal-user-input-password"
                  />
                </FormField>
              )}
              <div>
                <Label className="label-eyebrow">Status</Label>
                <div className="mt-2 flex gap-2">
                  {["active", "inactive"].map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setForm({ ...form, status: s })}
                      data-testid={`portal-user-status-${s}`}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        form.status === s
                          ? "border-primary/40 bg-primary/10 text-primary"
                          : "border-white/10 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {s === "active" ? "Aktiv" : "Inaktiv"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Zugewiesene Firmen</Label>
                <div className="mt-2 max-h-52 overflow-auto rounded-lg border border-white/[0.06] divide-y divide-white/[0.05]">
                  {businesses.length ? (
                    businesses.map((b) => (
                      <label
                        key={b.id}
                        className="flex items-start gap-3 px-3 py-2.5 hover:bg-white/[0.03] cursor-pointer"
                      >
                        <Checkbox
                          checked={form.assigned_customer_ids.includes(b.id)}
                          onCheckedChange={() => toggleAssigned(b.id)}
                          data-testid={`portal-user-assign-${b.id}`}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{b.business_name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {b.address}
                          </div>
                        </div>
                      </label>
                    ))
                  ) : (
                    <div className="p-4 text-xs text-muted-foreground text-center">
                      Noch keine Firmen angelegt. Bitte zuerst unter „Kunden“ eine Firma
                      hinzufügen.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="gap-2"
                disabled={saving}
                data-testid="portal-user-save-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => (!o ? setResetTarget(null) : null)}>
        <DialogContent className="max-w-md bg-card border-white/[0.06]">
          <form onSubmit={submitReset} data-testid="portal-reset-dialog">
            <DialogHeader>
              <DialogTitle>Passwort zurücksetzen</DialogTitle>
              <DialogDescription>
                Neues Passwort für <strong>{resetTarget?.email}</strong>. Der Kunde muss
                dieses Passwort für die nächste Anmeldung verwenden.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-3 space-y-1.5">
              <Label className="label-eyebrow">Neues Passwort</Label>
              <Input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                minLength={8}
                required
                className="bg-background/50"
                data-testid="portal-reset-new-password"
              />
              <p className="text-xs text-muted-foreground">Mindestens 8 Zeichen.</p>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setResetTarget(null)}>
                Abbrechen
              </Button>
              <Button type="submit" data-testid="portal-reset-submit">
                Passwort setzen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Plan override dialog */}
      <Dialog
        open={!!overrideTarget}
        onOpenChange={(o) => (!o ? setOverrideTarget(null) : null)}
      >
        <DialogContent className="max-w-md bg-card border-white/[0.06]">
          <form onSubmit={applyOverride} data-testid="portal-override-dialog">
            <DialogHeader>
              <DialogTitle>Tarif-Override</DialogTitle>
              <DialogDescription>
                Setzt einen manuellen Tarif für <strong>{overrideTarget?.email}</strong>.
                Der Override hat Vorrang vor der Stripe-Subscription und wird im
                Kundenportal deutlich als „Admin-Freischaltung" gekennzeichnet.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-3 space-y-2">
              <Label className="label-eyebrow">Override</Label>
              <div className="flex flex-wrap gap-2">
                {["", "starter", "business", "agency"].map((code) => {
                  const label = code === "" ? "Kein Override" : code.charAt(0).toUpperCase() + code.slice(1);
                  const active = pendingOverride === code;
                  return (
                    <button
                      type="button"
                      key={code || "none"}
                      onClick={() => setPendingOverride(code)}
                      data-testid={`override-choice-${code || "none"}`}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        active
                          ? "border-primary/40 bg-primary/10 text-primary"
                          : "border-white/10 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                „Kein Override" entfernt manuelle Freischaltungen und übergibt die
                Steuerung wieder an Stripe.
              </p>
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOverrideTarget(null)}
              >
                Abbrechen
              </Button>
              <Button type="submit" data-testid="portal-override-submit">
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, hint, children }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
      {hint ? <p className="text-xs text-muted-foreground mt-1.5">{hint}</p> : null}
    </div>
  );
}
